/**
 * Table primitives. `Table` wraps a horizontally-scrollable container (phones never get a
 * page-level horizontal scroll). Rows have a FIXED height (44px, 36px dense): cells are
 * single-line and truncate with an ellipsis, so a long description never makes the grid
 * ragged. Columns are user-resizable by default: once the table has painted, the header
 * widths are captured into a `<colgroup>`, the layout switches to `table-layout: fixed`,
 * and each header gets a drag handle that is also a focusable `role="separator"` (ArrowLeft /
 * ArrowRight nudge the column by 16px — F-20). The handle sits inside the `<th>`, so the
 * header names itself with `aria-labelledby` → a `<span>` around its text: the column's
 * accessible name stays exactly its text, never "Amount Resize column". `Th`/`Td` carry the
 * alignment classes — `numeric` right-aligns with tabular figures (§8 money convention),
 * `mono` for IDs/hashes. `Pager` is the shared Prev/Next footer with an `aria-live` range.
 */
import { ChevronLeft, ChevronRight } from './icons.ts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';

import { Button } from './button.tsx';

const MIN_COL_WIDTH = 64;
/** One keyboard nudge of a column resizer (F-20). */
const KEY_RESIZE_STEP = 16;

interface TableContextValue {
  resizable: boolean;
  widths: number[] | null;
  setWidth: (index: number, width: number) => void;
}

const TableContext = createContext<TableContextValue>({
  resizable: false,
  widths: null,
  setWidth: () => {},
});

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  dense?: boolean | undefined;
  stickyHeader?: boolean | undefined;
  /** Announced to assistive tech; visually hidden. */
  caption?: string | undefined;
  /** User-adjustable column widths (default on). */
  resizable?: boolean | undefined;
}

function headerCells(table: HTMLTableElement): HTMLTableCellElement[] {
  return Array.from(
    table.querySelectorAll<HTMLTableCellElement>(
      ':scope > thead > tr:first-child > th, :scope > thead > tr:first-child > td',
    ),
  );
}

export function Table({
  dense,
  stickyHeader,
  caption,
  resizable = true,
  className,
  children,
  ...rest
}: TableProps): JSX.Element {
  const tableRef = useRef<HTMLTableElement>(null);
  const [widths, setWidths] = useState<number[] | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Once the user has dragged a column, their widths win over any automatic re-measure.
  const userResized = useRef(false);
  // The wrapper width the frozen widths were measured against.
  const measuredAt = useRef(0);

  // The first measurement can land on fallback-font metrics or a not-yet-settled container.
  // Re-measure (drop the frozen widths) when a web font finishes loading and when the
  // wrapper's width changes, as long as the user has not resized anything by hand.
  useEffect(() => {
    if (!resizable) return;
    const remeasure = (): void => {
      if (!userResized.current) setWidths(null);
    };
    const fonts = document.fonts;
    fonts?.addEventListener('loadingdone', remeasure);
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && wrapRef.current) {
      // Fires once on observe (with the settled size) and again on every resize: if the
      // container is not the width the columns were measured at, measure again.
      observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width;
        if (width !== undefined && Math.abs(width - measuredAt.current) > 1) remeasure();
      });
      observer.observe(wrapRef.current);
    }
    return () => {
      fonts?.removeEventListener('loadingdone', remeasure);
      observer?.disconnect();
    };
  }, [resizable]);

  // After paint, freeze the browser's auto column widths into a colgroup so the layout can
  // go `fixed` (needed for ellipsis + resizing) without a visible jump. Re-runs whenever the
  // header cell count changes; a zero-width measurement (hidden, or jsdom) keeps auto layout.
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table || !resizable) return;
    const cells = headerCells(table);
    if (cells.length === 0 || (widths !== null && widths.length === cells.length)) return;
    const measured = cells.map((c) => c.getBoundingClientRect().width);
    if (measured.some((w) => !(w > 0))) return;
    measuredAt.current = wrapRef.current?.getBoundingClientRect().width ?? 0;
    // Ceil, not round: a width even 0.4px short of the content trips the ellipsis.
    setWidths(measured.map((w) => Math.ceil(w)));
  }, [resizable, widths, children]);

  const setWidth = useCallback((index: number, width: number) => {
    setWidths((prev) => {
      const table = tableRef.current;
      const base =
        prev ?? (table ? headerCells(table).map((c) => c.getBoundingClientRect().width) : []);
      if (index < 0 || index >= base.length || !Number.isFinite(width)) return prev;
      userResized.current = true;
      const next = base.slice();
      next[index] = Math.max(MIN_COL_WIDTH, Math.round(width));
      return next;
    });
  }, []);

  const fixed = resizable && widths !== null;

  return (
    <TableContext.Provider value={{ resizable, widths, setWidth }}>
      <div className="argus-table-wrap" ref={wrapRef}>
        <table
          ref={tableRef}
          className={[
            'argus-table',
            dense ? 'argus-table--dense' : '',
            stickyHeader ? 'argus-table--sticky' : '',
            fixed ? 'argus-table--fixed' : '',
            className ?? '',
          ]
            .filter(Boolean)
            .join(' ')}
          {...rest}
        >
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          {fixed ? (
            <colgroup>
              {widths.map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
          ) : null}
          {children}
        </table>
      </div>
    </TableContext.Provider>
  );
}

function cellClass(
  base: string | undefined,
  { numeric, mono, shrink }: CellProps,
): string | undefined {
  const cls = [
    base ?? '',
    numeric ? 'is-numeric' : '',
    mono ? 'is-mono' : '',
    shrink ? 'is-shrink' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return cls === '' ? undefined : cls;
}

export interface CellProps {
  numeric?: boolean | undefined;
  mono?: boolean | undefined;
  /** Collapse the column to its content width (auto layout only). */
  shrink?: boolean | undefined;
}

function ColumnResizer(): JSX.Element {
  const { widths, setWidth } = useContext(TableContext);
  const drag = useRef<{ index: number; startX: number; startWidth: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  function cellOf(el: HTMLElement): HTMLTableCellElement | null {
    return el.closest('th, td');
  }

  function currentWidth(cell: HTMLTableCellElement): number {
    return widths?.[cell.cellIndex] ?? cell.getBoundingClientRect().width;
  }

  function onPointerDown(event: PointerEvent<HTMLSpanElement>): void {
    const cell = cellOf(event.currentTarget);
    if (!cell) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { index: cell.cellIndex, startX: event.clientX, startWidth: currentWidth(cell) };
    setDragging(true);
  }

  function onPointerMove(event: PointerEvent<HTMLSpanElement>): void {
    if (!drag.current) return;
    setWidth(drag.current.index, drag.current.startWidth + (event.clientX - drag.current.startX));
  }

  function onPointerUp(event: PointerEvent<HTMLSpanElement>): void {
    if (!drag.current) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    drag.current = null;
    setDragging(false);
  }

  // F-20 keyboard resizing: ArrowLeft / ArrowRight nudge by KEY_RESIZE_STEP; Home / End are
  // swallowed as no-ops (there is no meaningful min/max to jump to, and the page must not
  // scroll under a focused handle).
  function onKeyDown(event: KeyboardEvent<HTMLSpanElement>): void {
    const delta =
      event.key === 'ArrowRight'
        ? KEY_RESIZE_STEP
        : event.key === 'ArrowLeft'
          ? -KEY_RESIZE_STEP
          : 0;
    if (delta === 0) {
      if (event.key === 'Home' || event.key === 'End') event.preventDefault();
      return;
    }
    const cell = cellOf(event.currentTarget);
    if (!cell) return;
    event.preventDefault();
    setWidth(cell.cellIndex, currentWidth(cell) + delta);
  }

  // The handle is a focusable separator INSIDE the <th>; the header keeps its own name via
  // `aria-labelledby` (see `Th`), so this label never joins the column's accessible name.
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      aria-valuemin={MIN_COL_WIDTH}
      tabIndex={0}
      className={['argus-table__resizer', dragging ? 'is-dragging' : ''].filter(Boolean).join(' ')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
    />
  );
}

export function Th({
  numeric,
  mono,
  shrink,
  className,
  children,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & CellProps): JSX.Element {
  const { resizable } = useContext(TableContext);
  const labelId = useId();
  // The header names itself from its text span, so the resizer's label stays out of it. A
  // caller-supplied `aria-label` / `aria-labelledby` wins (e.g. an icon-only actions column).
  const selfLabelled =
    resizable && rest['aria-label'] === undefined && rest['aria-labelledby'] === undefined;
  return (
    <th
      scope="col"
      className={cellClass(className, { numeric, mono, shrink })}
      aria-labelledby={selfLabelled ? labelId : undefined}
      {...rest}
    >
      {selfLabelled ? <span id={labelId}>{children}</span> : children}
      {resizable ? <ColumnResizer /> : null}
    </th>
  );
}

export function Td({
  numeric,
  mono,
  shrink,
  className,
  children,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & CellProps): JSX.Element {
  return (
    <td className={cellClass(className, { numeric, mono, shrink })} {...rest}>
      {children}
    </td>
  );
}

/** A full-width empty row (for a table whose surrounding chrome should stay put). */
export function TableEmptyRow({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}): JSX.Element {
  return (
    <tr className="argus-table__empty">
      <td colSpan={colSpan}>{children}</td>
    </tr>
  );
}

export interface PagerProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** e.g. "cases" — used in the range sentence. */
  noun?: string | undefined;
  disabled?: boolean | undefined;
}

export function Pager({
  page,
  pageSize,
  total,
  onPageChange,
  noun = 'rows',
  disabled = false,
}: PagerProps): JSX.Element | null {
  if (total <= 0) return null;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <nav className="argus-pager" aria-label="Pagination">
      <span aria-live="polite">
        Showing {from}–{to} of {total} {noun}
      </span>
      <span className="row">
        <Button
          size="sm"
          icon={<ChevronLeft size={14} aria-hidden="true" />}
          disabled={disabled || page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <span className="text-xs">
          Page {page} of {pages}
        </span>
        <Button
          size="sm"
          disabled={disabled || page >= pages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
          <ChevronRight size={14} aria-hidden="true" />
        </Button>
      </span>
    </nav>
  );
}
